// src/pages/Production/ProductCostReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151',
        cursor: 'pointer'
    },
    profitBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    }
};

function ProductCostReport() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [products, setProducts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubPurchases = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterialPurchases')),
            snap => {
                setRawMaterialPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubProducts();
            unsubRecipes();
            unsubRawMaterials();
            unsubPurchases();
        };
    }, [userData]);

    const getMaterialPurchasePrice = (materialId, materialName) => {
        const purchasesWithMaterial = rawMaterialPurchases
            .filter(p => p.lineItems && p.lineItems.some(item =>
                item.productId === materialId || item.name === materialName
            ))
            .sort((a, b) => {
                const dateA = a.purchaseDate?.toDate?.() || new Date(0);
                const dateB = b.purchaseDate?.toDate?.() || new Date(0);
                return dateB - dateA;
            });

        if (purchasesWithMaterial.length > 0) {
            const lineItem = purchasesWithMaterial[0].lineItems.find(item =>
                item.productId === materialId || item.name === materialName
            );
            if (lineItem) return lineItem.price;
        }
        return null;
    };

    const productCosts = useMemo(() => {
        const allMaterials = [
            ...rawMaterials.map(rm => ({
                name: rm.name,
                unitPrice: getMaterialPurchasePrice(rm.id, rm.name) || rm.unitPrice || 0
            })),
            ...products.filter(p => p.isRawMaterial).map(p => ({
                name: p.name,
                unitPrice: getMaterialPurchasePrice(p.id, p.name) || p.purchasePrice || 0
            }))
        ];

        return products.filter(p => !p.isRawMaterial).map(product => {
            const recipe = recipes.find(r =>
                r.product === product.name || r.productId === product.id
            );

            let rawMaterialCost = 0;
            let laborCost = 0;

            if (recipe && recipe.stages) {
                recipe.stages.forEach(stage => {
                    if (stage.inputMaterials) {
                        stage.inputMaterials.forEach(material => {
                            const rawMat = allMaterials.find(rm => rm.name === material.material);
                            if (rawMat) {
                                rawMaterialCost += (rawMat.unitPrice || 0) * (material.quantity || 0);
                            }
                        });
                    }
                    if (stage.type === 'internal' && stage.duration) {
                        laborCost += (stage.duration / 60) * 15;
                    }
                });
            }

            const overheadCost = (rawMaterialCost + laborCost) * 0.10;
            const totalCost = rawMaterialCost + laborCost + overheadCost;
            const salePrice = product.salePrice || 0;
            const profit = salePrice - totalCost;
            const profitMargin = salePrice > 0 ? (profit / salePrice) * 100 : 0;

            return {
                id: product.id,
                name: product.name,
                hasRecipe: !!recipe,
                rawMaterialCost,
                laborCost,
                overheadCost,
                totalCost,
                salePrice,
                profit,
                profitMargin
            };
        }).sort((a, b) => b.profitMargin - a.profitMargin);
    }, [products, recipes, rawMaterials, rawMaterialPurchases]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Maliyet verileri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>← Üretim Raporları</Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>Ürün Bazlı Maliyet Analizi</h2>
                <p style={{ color: '#64748b', marginTop: '8px', marginBottom: '24px' }}>
                    Ürünlerin maliyet dağılımı ve kâr marjları ({productCosts.length} ürün)
                </p>

                {productCosts.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                        <h3 style={{ color: '#374151' }}>Ürün Bulunamadı</h3>
                        <p>Henüz ürün tanımlanmamış.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün</th>
                                <th style={styles.th}>Reçete</th>
                                <th style={styles.th}>Hammadde</th>
                                <th style={styles.th}>İşçilik</th>
                                <th style={styles.th}>Genel Gider</th>
                                <th style={styles.th}>Toplam Maliyet</th>
                                <th style={styles.th}>Satış Fiyatı</th>
                                <th style={styles.th}>Kâr</th>
                                <th style={styles.th}>Kâr Marjı</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productCosts.map(product => (
                                <tr
                                    key={product.id}
                                    onClick={() => navigate(`/uretim/maliyet-hesaplama/${product.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{product.name}</td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.profitBadge,
                                            background: product.hasRecipe ? '#dcfce7' : '#fef3c7',
                                            color: product.hasRecipe ? '#166534' : '#92400e'
                                        }}>
                                            {product.hasRecipe ? 'Var' : 'Yok'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>{formatCurrency(product.rawMaterialCost)}</td>
                                    <td style={styles.td}>{formatCurrency(product.laborCost)}</td>
                                    <td style={styles.td}>{formatCurrency(product.overheadCost)}</td>
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{formatCurrency(product.totalCost)}</td>
                                    <td style={styles.td}>{formatCurrency(product.salePrice)}</td>
                                    <td style={{
                                        ...styles.td,
                                        fontWeight: '700',
                                        color: product.profit >= 0 ? '#059669' : '#dc2626'
                                    }}>
                                        {formatCurrency(product.profit)}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.profitBadge,
                                            background: product.profitMargin >= 0 ? '#dcfce7' : '#fef2f2',
                                            color: product.profitMargin >= 0 ? '#166534' : '#dc2626'
                                        }}>
                                            %{product.profitMargin.toFixed(1)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default ProductCostReport;
